/**
 * What is underfoot. The sim has no surface map - it does not need one - so audio derives it
 * from the same `MissionDef` geometry the terrain renderer paints from: ponds, rivers and
 * ice, bridges, roads, trenches, building floors, and otherwise the biome's ground.
 * Looked up once per footfall, never per frame.
 */

import { HAZARDS } from '../sim/tuning';
import type { Biome, MissionDef, PropKind } from '../sim/types';

export type Surface = 'snow' | 'ice' | 'sand' | 'rock' | 'grass' | 'mud' | 'water' | 'wood' | 'concrete';

export const SURFACES: readonly Surface[] = [
  'snow', 'ice', 'sand', 'rock', 'grass', 'mud', 'water', 'wood', 'concrete',
];

const BIOME_GROUND: Record<Biome, Surface> = { arctic: 'snow', desert: 'sand', temperate: 'grass' };
/** A trench floor: packed snow stays snow, but dug earth elsewhere is mud or bare sand. */
const TRENCH_FLOOR: Record<Biome, Surface> = { arctic: 'snow', desert: 'sand', temperate: 'mud' };

/** Walk-in structures with a floor of their own (half-extents, metres). */
const FLOORS: Partial<Record<PropKind, { hx: number; hz: number; surface: Surface }>> = {
  hangar: { hx: 7, hz: 5, surface: 'concrete' },
};
/** The deck covers the whole crossing lane (render/kit/pipeline.ts): planks underfoot wherever the sim lets him walk. */
const BRIDGE_HALF_WIDTH = HAZARDS.crossingHalfWidth + 0.2;

interface Floor {
  x: number;
  z: number;
  hx: number;
  hz: number;
  surface: Surface;
}

function distanceToPolyline(points: ReadonlyArray<readonly [number, number]>, x: number, z: number): number {
  let best = Infinity;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (!a || !b) continue;
    const vx = b[0] - a[0];
    const vz = b[1] - a[1];
    const len2 = vx * vx + vz * vz;
    const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((x - a[0]) * vx + (z - a[1]) * vz) / len2));
    const dx = x - (a[0] + vx * t);
    const dz = z - (a[1] + vz * t);
    best = Math.min(best, dx * dx + dz * dz);
  }
  return Math.sqrt(best);
}

export type SurfaceLookup = (x: number, z: number) => Surface;

/** Build the lookup for one mission: the few props that have floors are picked out once. */
export function createSurfaceLookup(def: MissionDef): SurfaceLookup {
  const floors: Floor[] = [];
  for (const prop of def.props) {
    if (prop.kind === 'bridge') {
      // The contract does not say which way a bridge runs, so accept either axis: the cross is small.
      const half = (prop.length ?? 6) / 2;
      floors.push({ x: prop.x, z: prop.z, hx: BRIDGE_HALF_WIDTH, hz: half, surface: 'wood' });
      floors.push({ x: prop.x, z: prop.z, hx: half, hz: BRIDGE_HALF_WIDTH, surface: 'wood' });
      continue;
    }
    const floor = FLOORS[prop.kind];
    if (floor) floors.push({ x: prop.x, z: prop.z, hx: floor.hx, hz: floor.hz, surface: floor.surface });
  }
  const frozen = def.biome === 'arctic';

  return (x, z) => {
    for (const f of floors) if (Math.abs(x - f.x) <= f.hx && Math.abs(z - f.z) <= f.hz) return f.surface;
    for (const barrier of def.waterBarriers) {
      if (distanceToPolyline(barrier.course.points, x, z) <= barrier.course.width / 2) return barrier.surface;
    }
    for (const pond of def.ponds) {
      const dx = x - pond.x;
      const dz = (z - pond.z) * 1.2;
      if (dx * dx + dz * dz < pond.r * pond.r * 0.85) return frozen ? 'ice' : 'water';
    }
    for (const road of def.roads) {
      // Ploughed gravel even in the arctic: "ice" underfoot must only ever mean frozen water (mission 6).
      if (distanceToPolyline(road.points, x, z) <= road.width / 2) return 'rock';
    }
    for (const trench of def.trenches) {
      if (distanceToPolyline(trench.points, x, z) <= trench.width / 2) return TRENCH_FLOOR[def.biome];
    }
    return BIOME_GROUND[def.biome];
  };
}
